import {XMarkIcon} from "@heroicons/react/20/solid";
import {ReactNode, useId} from "react";
import {OtsGoBot, OtsGoBotState} from "./OtsGoBot";

export type OtsGoBotBubbleProps = {
  title: ReactNode;
  children?: ReactNode;
  /** Buttons or links under the message. */
  actions?: ReactNode;
  /** Shown as the close (X) button when given. */
  onClose?: () => void;
  closeLabel?: string;
  /** GoBot beside the title; `hello` makes the bubble's arrival feel like GoBot showing up. */
  goBotState?: OtsGoBotState;
  /** Which side the tail points to, toward whatever opened the bubble. */
  tail?: "bottom-end" | "top-end" | "none";
  className?: string;
};

/**
 * GoBot speaking: a small non-modal card with GoBot, a title, a short message, and actions. Hosts position it; it is
 * labeled by its title so a screen reader announces who is talking and why.
 */
export const OtsGoBotBubble = ({
  title,
  children,
  actions,
  onClose,
  closeLabel = "Close",
  goBotState = "idle",
  tail = "none",
  className,
}: OtsGoBotBubbleProps) => {
  const titleId = useId();

  return <section
    role="dialog"
    aria-modal="false"
    aria-labelledby={titleId}
    className={[
      "relative w-[20rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-[var(--ots-outline-variant)]",
      "bg-[var(--ots-surface)] p-4 text-[var(--ots-text)]",
      "shadow-[0_12px_32px_-12px_rgb(15_23_42/0.4),0_1px_2px_rgb(15_23_42/0.06)]",
      className,
    ].filter(Boolean).join(" ")}>
    <div className="flex items-start gap-3">
      <OtsGoBot state={goBotState} size={40}/>
      <div className="min-w-0 flex-1 pt-0.5">
        <h2 id={titleId} className="font-[family-name:var(--ots-font-display)] text-sm/5 font-semibold text-[var(--ots-secondary)]">
          {title}
        </h2>
        {children != null && <div className="mt-1 text-[13px]/5 text-[var(--ots-text-muted)]">{children}</div>}
      </div>
      {onClose && <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="-mr-1.5 -mt-1.5 rounded-full p-1.5 text-[var(--ots-text-muted)] hover:bg-[var(--ots-neutral-container)] hover:text-[var(--ots-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]">
        <XMarkIcon aria-hidden="true" className="size-4"/>
      </button>}
    </div>
    {actions != null && <div className="mt-3 flex flex-wrap items-center justify-end gap-2">{actions}</div>}
    {tail !== "none" && <span
      aria-hidden="true"
      className={[
        "absolute right-6 size-3 rotate-45 border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]",
        tail === "bottom-end" ? "-bottom-1.5 border-b border-r" : "-top-1.5 border-l border-t",
      ].join(" ")}/>}
  </section>;
};
